import { RENDER_TIERS, tierRank, tierSatisfies } from '@stem/lab-schema/runtime';
const OVERRIDE_KEY = 'stem.lab.tier';
/**
 * Chooses a rendering tier.
 *
 * The signals are deliberately coarse, because the failure they are guarding
 * against is coarse: a Grade 7 learner on a shared Android phone over 2G, for
 * whom a 4 MB video and a canvas animation mean the lab never appears at all.
 * Getting the tier slightly wrong costs a little polish; getting it wrong in
 * the other direction costs the whole lesson.
 *
 * `saveData` is treated as decisive rather than as one input among several.
 * A user who has turned on Data Saver has told us something explicitly, and
 * inferring past it would be overriding a stated preference with a guess.
 */
export function decideTier(signals = {}) {
    const detected = detect(signals);
    if (signals.override && RENDER_TIERS.includes(signals.override)) {
        return {
            tier: signals.override,
            reason: 'You chose this quality setting.',
            detected,
            signals,
        };
    }
    return { tier: detected, reason: reasonFor(detected, signals), detected, signals };
}
function detect(signals) {
    if (signals.saveData)
        return 'text';
    const slowNetwork = signals.effectiveType === 'slow-2g' || signals.effectiveType === '2g';
    if (slowNetwork)
        return 'text';
    const weakDevice = (signals.deviceMemory !== undefined && signals.deviceMemory <= 1) ||
        (signals.hardwareConcurrency !== undefined && signals.hardwareConcurrency <= 2);
    if (weakDevice)
        return 'text';
    // Reduced motion is not a performance signal — it is an accessibility one —
    // so it caps the tier at 2d rather than dropping to text. A learner who does
    // not want parallax still wants the bar graph.
    if (signals.reducedMotion)
        return '2d';
    if (signals.effectiveType === '3g')
        return '2d';
    const strongDevice = (signals.deviceMemory ?? 4) >= 8 && (signals.hardwareConcurrency ?? 4) >= 8;
    return strongDevice ? '2.5d' : '2d';
}
function reasonFor(tier, signals) {
    if (signals.saveData)
        return 'Data Saver is on, so this lab is showing its lightest version.';
    if (signals.effectiveType === 'slow-2g' || signals.effectiveType === '2g') {
        return 'Your connection is slow, so this lab is showing its lightest version.';
    }
    if ((signals.deviceMemory ?? 4) <= 1 || (signals.hardwareConcurrency ?? 4) <= 2) {
        return 'This device has limited memory, so this lab is showing its lightest version.';
    }
    if (signals.reducedMotion)
        return 'You have asked for reduced motion, so animations are off.';
    if (tier === '2.5d')
        return 'Your device and connection can handle the full experience.';
    return 'Standard quality.';
}
/** Reads what the browser will tell us. Safe to call during render. */
export function readSignals() {
    if (typeof navigator === 'undefined')
        return {};
    const connection = navigator.connection;
    return {
        effectiveType: connection?.effectiveType,
        saveData: connection?.saveData,
        deviceMemory: navigator.deviceMemory,
        hardwareConcurrency: navigator.hardwareConcurrency,
        reducedMotion: typeof window !== 'undefined' &&
            window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
        override: readOverride(),
    };
}
export function readOverride() {
    if (typeof localStorage === 'undefined')
        return null;
    const stored = localStorage.getItem(OVERRIDE_KEY);
    return stored && RENDER_TIERS.includes(stored)
        ? stored
        : null;
}
export function writeOverride(tier) {
    if (typeof localStorage === 'undefined')
        return;
    if (tier)
        localStorage.setItem(OVERRIDE_KEY, tier);
    else
        localStorage.removeItem(OVERRIDE_KEY);
}
/**
 * The tier a given lab will actually render at.
 *
 * A lab may declare a `minTier` it cannot teach below — a titration honestly
 * needs pixels. Where the detected tier is weaker, the lab is rendered at its
 * minimum anyway and the caller is told, because showing a learner an empty box
 * is worse than showing them something slow.
 */
export function effectiveTier(decision, minTier) {
    if (tierSatisfies(decision.tier, minTier))
        return decision;
    return {
        ...decision,
        tier: minTier,
        reason: `This lab needs at least the "${minTier}" experience, so it is loading that even though your connection looks slow.`,
    };
}
export { tierRank, tierSatisfies, RENDER_TIERS };
//# sourceMappingURL=tier.js.map